import { useMemo, useState } from "react";

// Generic list shell (search + page-size + sortable columns + pagination) shared by the admin
// Users/Roles pages, so both present the same table regardless of what data they hold. All filtering,
// sorting and paging happens client-side — the row counts here are small (users, roles), never dataset
// rows, so there is no reason to push this to the server.
export default function DataTable({
  columns,
  rows,
  searchKeys,
  searchPlaceholder = "Search...",
  pageSizeOptions = [10, 25, 50],
  defaultPageSize = 10,
  emptyMessage = "No records found.",
}) {
  const [query, setQuery] = useState("");
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(null); // { key, dir: 1 | -1 }

  const keys = searchKeys && searchKeys.length ? searchKeys : columns.map((c) => c.key);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => keys.some((k) => String(r[k] ?? "").toLowerCase().includes(q)));
  }, [rows, query, keys]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const { key, dir } = sort;
    return [...filtered].sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return -1 * dir;
      if (bv == null) return 1 * dir;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [filtered, sort]);

  const total = sorted.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const clampedPage = Math.min(page, pageCount);
  const start = (clampedPage - 1) * pageSize;
  const pageRows = sorted.slice(start, start + pageSize);

  function toggleSort(key, sortable) {
    if (!sortable) return;
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 1 };
      if (prev.dir === 1) return { key, dir: -1 };
      return null;
    });
  }

  function changePageSize(n) {
    setPageSize(n);
    setPage(1);
  }

  function changeQuery(v) {
    setQuery(v);
    setPage(1);
  }

  // A short window of page numbers around the current page, so the footer stays a fixed width instead
  // of growing with the total page count.
  const pageNumbers = useMemo(() => {
    const span = 2;
    const lo = Math.max(1, clampedPage - span);
    const hi = Math.min(pageCount, clampedPage + span);
    const out = [];
    for (let i = lo; i <= hi; i++) out.push(i);
    return out;
  }, [clampedPage, pageCount]);

  return (
    <div className="dt-card">
      <div className="dt-toolbar">
        <div className="dt-toolbar-left">
          Show{" "}
          <select
            className="dt-pagesize"
            value={pageSize}
            onChange={(e) => changePageSize(Number(e.target.value))}
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>{" "}
          entries
        </div>
        <div className="dt-search">
          Search:{" "}
          <input
            value={query}
            onChange={(e) => changeQuery(e.target.value)}
            placeholder={searchPlaceholder}
          />
        </div>
      </div>

      <div className="dt-table-wrap">
        <table className="dt-table">
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={c.sortable ? "sortable" : ""}
                  style={{ width: c.width, textAlign: c.align || "left" }}
                  onClick={() => toggleSort(c.key, c.sortable)}
                >
                  {c.label}
                  {c.sortable ? (sort?.key === c.key ? (sort.dir === 1 ? " ▲" : " ▼") : " ⇅") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row) => (
              <tr key={row.id ?? row.rowKey}>
                {columns.map((c) => (
                  <td key={c.key} style={{ textAlign: c.align || "left" }}>
                    {c.render ? c.render(row) : row[c.key]}
                  </td>
                ))}
              </tr>
            ))}
            {pageRows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="dt-empty">
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="dt-footer">
        <div>
          {total === 0
            ? "No entries"
            : `Showing ${start + 1} to ${Math.min(start + pageSize, total)} of ${total} entries`}
        </div>
        <div className="dt-pager">
          <button disabled={clampedPage <= 1} onClick={() => setPage(clampedPage - 1)}>
            Previous
          </button>
          {pageNumbers[0] > 1 && <span className="dt-pager-ellipsis">…</span>}
          {pageNumbers.map((n) => (
            <button key={n} className={n === clampedPage ? "active" : ""} onClick={() => setPage(n)}>
              {n}
            </button>
          ))}
          {pageNumbers[pageNumbers.length - 1] < pageCount && <span className="dt-pager-ellipsis">…</span>}
          <button disabled={clampedPage >= pageCount} onClick={() => setPage(clampedPage + 1)}>
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
